import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, CheckCircle, XCircle, AlertTriangle, Search, ZoomIn, ZoomOut, Loader2 } from 'lucide-react';
import { useDisputeDetail, DisputeStatus } from '../../../hooks/useDisputes';

const STATUS_CONFIG: Record<DisputeStatus, { label: string; color: string; bgColor: string; icon: React.ElementType }> = {
  PENDING_OCR: { label: 'Processando OCR', color: 'text-yellow-600 dark:text-yellow-400', bgColor: 'bg-yellow-50 dark:bg-yellow-900/20', icon: Clock },
  PENDING_VERIFICATION: { label: 'Verificando Pagamento', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-50 dark:bg-blue-900/20', icon: Search },
  VERIFIED: { label: 'Pagamento Verificado', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-50 dark:bg-green-900/20', icon: CheckCircle },
  UNVERIFIED: { label: 'Não Verificado', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-50 dark:bg-orange-900/20', icon: AlertTriangle },
  OCR_FAILED: { label: 'Erro no Processamento', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-50 dark:bg-orange-900/20', icon: AlertTriangle },
  DUPLICATE_IMAGE: { label: 'Comprovante Duplicado', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-50 dark:bg-red-900/20', icon: XCircle },
  DUPLICATE_E2E: { label: 'Transação Duplicada', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-50 dark:bg-red-900/20', icon: XCircle },
  PENDING_REVIEW: { label: 'Em Análise', color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-50 dark:bg-purple-900/20', icon: Clock },
  PENDING_SECOND_APPROVAL: { label: 'Aguardando 2ª Aprovação', color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-50 dark:bg-purple-900/20', icon: Clock },
  APPROVED: { label: 'Aprovado — Valor Creditado', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-50 dark:bg-green-900/20', icon: CheckCircle },
  REJECTED: { label: 'Rejeitado', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-50 dark:bg-red-900/20', icon: XCircle },
  CANCELLED: { label: 'Cancelado', color: 'text-gray-600 dark:text-gray-400', bgColor: 'bg-gray-50 dark:bg-gray-900/20', icon: XCircle },
  ALREADY_CREDITED: { label: 'Já Creditado Anteriormente', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-50 dark:bg-green-900/20', icon: CheckCircle },
};

const REJECTION_REASONS: Record<string, string> = {
  ILLEGIBLE: 'Comprovante ilegível',
  FAKE: 'Comprovante falso ou adulterado',
  ALREADY_CREDITED: 'Valor já creditado anteriormente',
  NOT_FOUND: 'Pagamento não encontrado',
  DATA_MISMATCH: 'Dados não conferem',
  OTHER: 'Outro motivo',
};

function formatCurrency(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Timeline steps for progress visualization
const TIMELINE_STEPS: { status: DisputeStatus[]; label: string }[] = [
  { status: ['PENDING_OCR'], label: 'Enviado' },
  { status: ['PENDING_VERIFICATION'], label: 'Processado' },
  { status: ['VERIFIED', 'UNVERIFIED', 'OCR_FAILED'], label: 'Verificado' },
  { status: ['PENDING_REVIEW', 'PENDING_SECOND_APPROVAL'], label: 'Em Análise' },
  { status: ['APPROVED', 'REJECTED', 'CANCELLED', 'ALREADY_CREDITED'], label: 'Concluído' },
];

function getStepIndex(status: DisputeStatus): number {
  for (let i = 0; i < TIMELINE_STEPS.length; i++) {
    if (TIMELINE_STEPS[i].status.includes(status)) return i;
  }
  return 0;
}

export function DisputeDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: dispute, isLoading } = useDisputeDetail(id || '');
  const [zoom, setZoom] = useState(1);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!dispute) {
    return (
      <div className="max-w-lg mx-auto p-4 text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Contestação não encontrada</p>
        <button onClick={() => navigate('/banking/disputes')} className="mt-4 text-primary-500 hover:text-primary-600 font-medium">
          Voltar para lista
        </button>
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[dispute.status];
  const StatusIcon = statusCfg.icon;
  const currentStep = getStepIndex(dispute.status);

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/banking/disputes')} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
          <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </button>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Detalhes da Contestação</h1>
      </div>

      {/* Status Banner */}
      <div className={`p-4 rounded-lg ${statusCfg.bgColor} flex items-center gap-3`}>
        <StatusIcon className={`w-6 h-6 ${statusCfg.color}`} />
        <div>
          <p className={`font-semibold ${statusCfg.color}`}>{statusCfg.label}</p>
          {dispute.status === 'APPROVED' && dispute.netAmountCents && (
            <p className="text-sm text-green-700 dark:text-green-300">
              Creditado: {formatCurrency(dispute.netAmountCents)} (líquido)
            </p>
          )}
        </div>
      </div>

      {/* Rejection Reason */}
      {dispute.status === 'REJECTED' && dispute.rejectionReason && (
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <p className="font-medium text-red-700 dark:text-red-400">Motivo da Rejeição</p>
          <p className="text-sm text-red-600 dark:text-red-300 mt-1">
            {REJECTION_REASONS[dispute.rejectionReason] || dispute.rejectionReason}
          </p>
          {dispute.rejectionReasonDetail && (
            <p className="text-sm text-red-600 dark:text-red-300 mt-1 italic">
              "{dispute.rejectionReasonDetail}"
            </p>
          )}
        </div>
      )}

      {/* Progress Timeline */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Progresso</h3>
        <div className="flex items-center gap-1">
          {TIMELINE_STEPS.map((step, idx) => (
            <div key={step.label} className="flex-1 flex flex-col items-center">
              <div className={`w-full h-2 rounded-full ${
                idx <= currentStep
                  ? dispute.status === 'REJECTED' || dispute.status === 'CANCELLED'
                    ? 'bg-red-400 dark:bg-red-500'
                    : 'bg-primary-500'
                  : 'bg-gray-200 dark:bg-gray-700'
              }`} />
              <span className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 text-center">{step.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Claimed Data Cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">Valor</p>
          <p className="text-lg font-bold text-gray-900 dark:text-white">{formatCurrency(dispute.claimedAmountCents)}</p>
        </div>
        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">Tipo</p>
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            {dispute.claimedType === 'DEPOSIT' ? 'Depósito' : 'Transferência'}
          </p>
        </div>
        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">Data Informada</p>
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {new Date(dispute.claimedDate).toLocaleDateString('pt-BR')}
          </p>
        </div>
        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">Criado em</p>
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {formatDate(dispute.createdAt)}
          </p>
        </div>
      </div>

      {/* Receipt Image */}
      {dispute.imagePath && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Comprovante</h3>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <ZoomOut className="w-4 h-4 text-gray-500" />
              </button>
              <button
                onClick={() => setZoom((z) => Math.min(3, z + 0.25))}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <ZoomIn className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>
          <div className="overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 max-h-96">
            <img
              src={`/api/v1/banking/disputes/${dispute.id}/image`}
              alt="Comprovante"
              className="mx-auto transition-transform"
              style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
        </div>
      )}

      {/* Credit Info (if approved) */}
      {dispute.status === 'APPROVED' && dispute.creditedAmountCents && (
        <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 space-y-2">
          <h3 className="font-medium text-green-700 dark:text-green-400">Crédito Realizado</h3>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div>
              <p className="text-green-600 dark:text-green-300 text-xs">Bruto</p>
              <p className="font-semibold text-green-800 dark:text-green-200">{formatCurrency(dispute.creditedAmountCents)}</p>
            </div>
            <div>
              <p className="text-green-600 dark:text-green-300 text-xs">Taxa (5%)</p>
              <p className="font-semibold text-green-800 dark:text-green-200">{formatCurrency(dispute.feeCents || 0)}</p>
            </div>
            <div>
              <p className="text-green-600 dark:text-green-300 text-xs">Líquido</p>
              <p className="font-semibold text-green-800 dark:text-green-200">{formatCurrency(dispute.netAmountCents || 0)}</p>
            </div>
          </div>
          {dispute.creditedAt && (
            <p className="text-xs text-green-600 dark:text-green-300">
              Creditado em {formatDate(dispute.creditedAt)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default DisputeDetail;
